import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Globe, Sparkles } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import type { CreateEventRequest } from "@/services/api/events";
import { getUserSettings } from "@/services/api/userSettings";
import { EventTypeSelector } from "./EventTypeSelector";
import { BabyNamesSection } from "@/pages/customer/event/general/sections/BabyNamesSection";
import { getDefaultTemplateForEventType, type EventTypeChoice } from "@/lib/event-templates";

interface CreateEventFormProps {
  onSubmit: (data: CreateEventRequest) => Promise<void>;
  onCancel: () => void;
  isCreating: boolean;
  /**
   * JUB-36: the type chosen in EventTypeModal, the first step of creation.
   * Seeds includesWedding/includesBaptism so the form opens already adapted
   * (baby names for baptism, couple names for wedding) instead of always
   * defaulting to wedding-only; the checkboxes below still let the customer
   * change their mind before submitting.
   */
  initialEventType?: EventTypeChoice;
}

type InvitationLanguage = 'ro' | 'en';

type CreateEventFormData = {
  name: string;
  date: string;
  includesWedding: boolean;
  includesBaptism: boolean;
  babyCount: 1 | 2;
  starBride?: string;
  starGroom?: string;
  starBaptism?: string;
  starBaptism2?: string;
  description?: string;
  defaultLanguage: InvitationLanguage;
};

export function CreateEventForm({
  onSubmit,
  onCancel,
  isCreating,
  initialEventType = "wedding",
}: CreateEventFormProps) {
  const { t } = useTranslation();

  const initialIncludesWedding = initialEventType !== "baptism";
  const initialIncludesBaptism = initialEventType === "baptism" || initialEventType === "both";

  // Create schema with translated messages
  const createEventSchema = useMemo(() => z.object({
    name: z.string().min(1, t('validation.eventNameRequired')),
    date: z.string().min(1, t('validation.eventDateRequired')),
    includesWedding: z.boolean(),
    includesBaptism: z.boolean(),
    babyCount: z.union([z.literal(1), z.literal(2)]),
    starBride: z.string().optional(),
    starGroom: z.string().optional(),
    starBaptism: z.string().optional(),
    starBaptism2: z.string().optional(),
    description: z.string().optional(),
    defaultLanguage: z.enum(['ro', 'en']),
  }).refine((data) => data.includesWedding || data.includesBaptism, {
    message: t('events.form.eventTypeRequired'),
    path: ['includesBaptism'],
  }).refine((data) => !data.includesBaptism || (data.starBaptism?.trim().length ?? 0) > 0, {
    message: t('events.form.babyNameRequired'),
    path: ['starBaptism'],
  }).refine(
    (data) => !data.includesBaptism || data.babyCount !== 2 || (data.starBaptism2?.trim().length ?? 0) > 0,
    {
      message: t('events.form.babyNameRequired'),
      path: ['starBaptism2'],
    },
  ), [t]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
    getFieldState,
  } = useForm<CreateEventFormData>({
    resolver: zodResolver(createEventSchema),
    defaultValues: {
      includesWedding: initialIncludesWedding,
      includesBaptism: initialIncludesBaptism,
      babyCount: 1,
      // JUB-81: the invitation language is an explicit choice, RO by default —
      // never silently inherited from the organizer's UI language.
      defaultLanguage: 'ro',
    },
  });

  const includesWedding = watch('includesWedding');
  const includesBaptism = watch('includesBaptism');
  const babyCount = watch('babyCount');
  const starBaptism = watch('starBaptism');
  const starBaptism2 = watch('starBaptism2');
  const defaultLanguage = watch('defaultLanguage');

  // Pre-fill the invitation language from the account's explicit
  // "default language for new events" setting (RO unless the organizer
  // changed it in Account Settings); the organizer can still override it here.
  useEffect(() => {
    let cancelled = false;
    const loadUserSettings = async () => {
      try {
        const settings = await getUserSettings();
        if (cancelled || getFieldState('defaultLanguage').isDirty) return;
        if (settings.defaultLanguage === 'ro' || settings.defaultLanguage === 'en') {
          setValue('defaultLanguage', settings.defaultLanguage);
        }
      } catch {
        // Silently fail - the select keeps its RO default
      }
    };
    loadUserSettings();
    return () => {
      cancelled = true;
    };
  }, [getFieldState, setValue]);

  // Couple labels: Mother/Father for baptism-only, Bride/Groom when wedding is included
  const firstPersonLabel = includesBaptism && !includesWedding ? t('events.form.mother') : t('events.form.brideName');
  const secondPersonLabel = includesBaptism && !includesWedding ? t('events.form.father') : t('events.form.groomName');
  const firstPersonPlaceholder = includesBaptism && !includesWedding ? t('events.form.mother') : t('events.brideNamePlaceholder');
  const secondPersonPlaceholder = includesBaptism && !includesWedding ? t('events.form.father') : t('events.groomNamePlaceholder');

  const onFormSubmit = async (data: CreateEventFormData) => {
    const eventData: CreateEventRequest = {
      name: data.name,
      date: data.date,
      includesWedding: data.includesWedding,
      includesBaptism: data.includesBaptism,
      babyCount: data.includesBaptism ? data.babyCount : undefined,
      starBride: data.starBride || undefined,
      starGroom: data.starGroom || undefined,
      starBaptism: data.includesBaptism ? data.starBaptism || undefined : undefined,
      starBaptism2: data.includesBaptism && data.babyCount === 2 ? data.starBaptism2 || undefined : undefined,
      description: data.description || undefined,
      currency: 'RON',
      country: 'ROU', // ISO 3166-1 alpha-3 code for Romania
      defaultLanguage: data.defaultLanguage,
      // JUB-36: derived from the current checkboxes (not the modal's original
      // pick), so changing type in-form still lands on the matching template.
      theme: getDefaultTemplateForEventType(data.includesWedding, data.includesBaptism),
    };
    await onSubmit(eventData);
    reset();
  };

  return (
    <Card className="mb-8 border-2 shadow-xl">
      <CardHeader className="bg-gradient-to-r from-pink-50 to-rose-50 dark:from-pink-950/20 dark:to-rose-950/20 border-b rounded-t-lg">
        <div className="flex items-center space-x-3">
          <Sparkles className="h-6 w-6 text-pink-600 dark:text-pink-400" />
          <div>
            <CardTitle className="text-2xl">{t('events.create')}</CardTitle>
            <CardDescription className="mt-1">{t('events.createDescription')}</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-6">
        <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
          <EventTypeSelector
            includesWedding={includesWedding}
            includesBaptism={includesBaptism}
            babyCount={includesBaptism ? babyCount : 1}
            onIncludesWeddingChange={(checked) => setValue('includesWedding', checked)}
            onIncludesBaptismChange={(checked) => setValue('includesBaptism', checked)}
            onBabyCountChange={(count) => setValue('babyCount', count)}
          />
          {errors.includesBaptism && (
            <p className="text-sm text-destructive">{errors.includesBaptism.message}</p>
          )}
          {includesBaptism && (
            <>
              <BabyNamesSection
                babyCount={babyCount === 1 || babyCount === 2 ? babyCount : 1}
                babyName={starBaptism || ""}
                babyName2={starBaptism2 || ""}
                onBabyNameChange={(value) => setValue('starBaptism', value, { shouldValidate: true })}
                onBabyName2Change={(value) => setValue('starBaptism2', value, { shouldValidate: true })}
              />
              {errors.starBaptism && (
                <p className="text-sm text-destructive">{errors.starBaptism.message}</p>
              )}
              {errors.starBaptism2 && (
                <p className="text-sm text-destructive">{errors.starBaptism2.message}</p>
              )}
            </>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name">
                {t('events.name')} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="name"
                {...register('name')}
                placeholder={t('events.namePlaceholder')}
                className="mt-1"
                data-testid="event-name-input"
              />
              {errors.name && (
                <p className="text-sm text-destructive mt-1">{errors.name.message}</p>
              )}
            </div>
            <div>
              <Label htmlFor="date">
                {t('events.date')} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="date"
                type="date"
                {...register('date')}
                className="mt-1"
                data-testid="event-date-input"
              />
              {errors.date && (
                <p className="text-sm text-destructive mt-1">{errors.date.message}</p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="starBride">
                {firstPersonLabel}
              </Label>
              <Input
                id="starBride"
                {...register('starBride')}
                placeholder={firstPersonPlaceholder}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="starGroom">
                {secondPersonLabel}
              </Label>
              <Input
                id="starGroom"
                {...register('starGroom')}
                placeholder={secondPersonPlaceholder}
                className="mt-1"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="description">
              {t('events.description')}
            </Label>
            <textarea
              id="description"
              {...register('description')}
              placeholder={t('events.descriptionPlaceholder')}
              className="mt-1 w-full min-h-[100px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            />
          </div>

          {/* JUB-81: explicit invitation language (RO default) */}
          <div>
            <Label htmlFor="defaultLanguage" className="flex items-center gap-2">
              <Globe className="h-4 w-4 text-pink-600 dark:text-pink-400" />
              {t('events.form.defaultLanguage')}
            </Label>
            <Select
              value={defaultLanguage}
              onValueChange={(value) =>
                setValue('defaultLanguage', value as InvitationLanguage, { shouldDirty: true })
              }
            >
              <SelectTrigger
                id="defaultLanguage"
                className="mt-1 w-full md:w-64"
                data-testid="event-language-select-trigger"
              >
                <SelectValue>{defaultLanguage === "en" ? "English" : "Română"}</SelectValue>
              </SelectTrigger>
              <SelectContent data-testid="event-language-select-content">
                <SelectItem value="ro" data-testid="event-language-option-ro">Română</SelectItem>
                <SelectItem value="en" data-testid="event-language-option-en">English</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground mt-1">
              {t('events.form.defaultLanguageDescription')}
            </p>
          </div>

          <div className="flex justify-end space-x-2 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
            >
              {t('common.cancel')}
            </Button>
            <Button 
              type="submit" 
              disabled={isCreating}
              className="bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white shadow-lg shadow-pink-500/50"
              data-testid="event-create-submit-button"
            >
              {isCreating ? t('common.loading') : t('events.create')}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

